import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import validator from 'validator'
import { Col, Row } from 'reactstrap'
import '../../App.css'

import ConversationalForm from './ConversationalForm'
import Helmet from '../../Component/Helmet'
import Image from './Image'
import BottomImage from './BottomImage'
import NavBar from './NavBar'
import NylasScheduler from './NylasScheduler'
import ErrorPage from '../../Component/ErrorPage'
import AuthService from '../../services/AuthService'
import Loader from '../../Component/Loader'
import * as s from './Chatbot.styles'
import { setCandidateId } from '../../redux/reducers/candidateSlice'

const Auth = new AuthService()

const Chatbot = (props) => {
  const dispatch = useDispatch()
  const params = useParams()
  const location = useLocation()
  // UI state
  const [isPreview, setIsPreview] = useState(false)
  const [isIframe, setIsIframe] = useState(false)
  const [status, setStatus] = useState('fetching')
  const [httpStatusCode, setHttpStatusCode] = useState(null)

  // chatbot offer related state
  const [offerName, setOfferName] = useState('')
  const [steps, setSteps] = useState([])
  const [sentences, setSentences] = useState(null)
  const [mandatorySteps, setMandatorySteps] = useState(null)
  const [careersUrl, setCareersUrl] = useState(null)
  const [alternativeOffers, setAlternativeOffers] = useState(null)
  const [messengerAppId, setMessengerAppId] = useState(null)
  const [messengerPageId, setMessengerPageId] = useState(null)
  const [messengerActive, setMessengerActive] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [standalone, setStandalone] = useState(null)
  const [teamId, setTeamId] = useState(null)
  const [offerActive, setOfferActive] = useState(false)
  const [nylasScheduler, setNylasScheduler] = useState(null)
  const [applicationDone, setApplicationDone] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState(null)
  const [phoneActive, setPhoneActive] = useState(null)
  const [offerId, setOfferId] = useState(props.offer?._id? props.offer?._id:params.id)
  const [chatbotType, setChatbotType] = useState(null)
  const [reactivationId, setReactivationId] = useState(null)
  const [candidate, setCandidate] = useState(null)
  const [requiredAtsFieldsCandidate, setRequiredAtsFieldsCandidate] = useState(
    [],
  )
  const [requiredAtsFieldsApplication, setRequiredAtsFieldsApplication] =
    useState([])
  const [requireCandidatePassword, setRequireCandidatePassword] =
    useState(false)


  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const preview = !!query.get('preview')

    setIsPreview(preview)
    setIsIframe(!!query.get('iframe'))

    // check for valid ID
    if (!offerId || !validator.isMongoId(offerId)) {
      setStatus('error')
      setHttpStatusCode(404)
    } else if (location.pathname.substring(0, 5) === '/chat') {
      Auth.fetch(`/lambda-conversational-form-chat/${offerId}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
        .then((conversationalFormChat) => {
          if (conversationalFormChat.notFound) {
            setStatus('error')
            setHttpStatusCode(404)
          } else {
            setOfferName(conversationalFormChat.name)
            setSteps(conversationalFormChat.steps)
            setMandatorySteps([])
            setStandalone(conversationalFormChat.standalone)
            setSentences(conversationalFormChat.sentences)
            setCareersUrl(conversationalFormChat.careersUrl)
            setCompanyId(conversationalFormChat.companyId)
            setOfferActive(conversationalFormChat.active)
            setReactivationId(conversationalFormChat.reactivationId)

            const { candidate: CandidateResponse } = Auth.getProfile()
            dispatch(setCandidateId(params.id))
            setCandidate(CandidateResponse)
            setChatbotType('reactivation')

            setHttpStatusCode(200)
            setStatus('fetched')
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e)
          setHttpStatusCode(500)
          setStatus('error')
        })
    } else {
      // fetch conversational form information
      Auth.fetch(
        `/lambda-conversational-form/${offerId}${
          preview ? '?preview=true' : ''
        }`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      )
        .then((conversationalForm) => {
          if (conversationalForm.notFound) {
            setStatus('error')
            setHttpStatusCode(404)
          } else {
            setOfferName(conversationalForm.name)
            setSteps(conversationalForm.steps)
            setMandatorySteps(conversationalForm.mandatorySteps)
            setStandalone(conversationalForm.standalone)
            setSentences(conversationalForm.sentences)
            setCareersUrl(conversationalForm.careersUrl)
            setAlternativeOffers(conversationalForm.alternativeOffers)
            setMessengerActive(conversationalForm.messengerActive)
            setMessengerPageId(conversationalForm.messengerAppId)
            setMessengerAppId(conversationalForm.messengerPageId)
            setPhoneActive(conversationalForm.phoneActive)
            setCompanyId(conversationalForm.companyId)
            setTeamId(conversationalForm.teamId)
            setOfferActive(conversationalForm.active)
            setPhoneNumber(conversationalForm.phoneNumber)
            setRequiredAtsFieldsCandidate(
              conversationalForm.requiredAtsFieldsCandidate,
            )
            setRequiredAtsFieldsApplication(
              conversationalForm.requiredAtsFieldsApplication,
            )
            setRequireCandidatePassword(
              conversationalForm.requireCandidatePassword,
            )
            setChatbotType('offer')
            setHttpStatusCode(200)
            setStatus('fetched')
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e)
          setHttpStatusCode(500)
          setStatus('error')
        })
    }
  }, [dispatch, location.pathname, location.search, params.id])

  // manage UI rendering
  if (status === 'fetching') {
    return <Loader />
  }
  if (status === 'error') {
    return <ErrorPage {...props} statusCode={httpStatusCode} />
  }

  let content = ''

  if (steps.length > 0) {
    content = (
      <ConversationalForm
        {...props}
        offerId={offerId}
        offerActive={offerActive}
        offerName={offerName}
        mandatorySteps={mandatorySteps}
        steps={steps}
        sentences={sentences}
        careersUrl={careersUrl}
        alternativeOffers={alternativeOffers}
        messengerAppId={messengerAppId}
        messengerPageId={messengerPageId}
        messengerActive={messengerActive}
        phoneNumber={phoneNumber}
        phoneActive={phoneActive}
        preview={isPreview}
        style={standalone.style}
        companyId={companyId}
        teamId={teamId}
        setNylasScheduler={setNylasScheduler}
        setApplicationDone={setApplicationDone}
        chatbotType={chatbotType}
        candidate={candidate}
        reactivationId={reactivationId}
        requiredAtsFieldsCandidate={requiredAtsFieldsCandidate}
        requiredAtsFieldsApplication={requiredAtsFieldsApplication}
        requireCandidatePassword={requireCandidatePassword}
      />
    )
  }

  if (nylasScheduler) {
    content = <NylasScheduler url={nylasScheduler} />
  }

  return (
    <s.ChatbotWrapper>
      <Helmet faviconUrl={standalone?.faviconId} name={offerName} />
      <NavBar
        url={standalone?.topLogoId}
        style={standalone.style}
        iframe={isIframe}
        applicationDone={applicationDone}
      />
      <Row className="content">
        <Col md="3" className="col-image">
          <Image url={standalone?.leftTopLogoId} />
          <Image url={standalone?.leftLogoId} />
          <Image url={standalone?.leftBottomLogoId} />
        </Col>
        <Col
          md={{ size: 6, offset: 0 }}
          sm={{ span: 12, offset: 0 }}
          className="col-chat"
        >
          <header className="App-header">{content}</header>
        </Col>
        <Col md={{ size: 3, offset: 0 }} className="col-image">
          <Image url={standalone?.rightTopLogoId} />
          <Image url={standalone?.rightLogoId} />
          <Image url={standalone?.rightBottomLogoId} />
        </Col>
        {standalone?.bottomImageId ? (
          <BottomImage url={standalone.bottomImageId} />
        ) : (
          ''
        )}
      </Row>
    </s.ChatbotWrapper>
  )
}

export default Chatbot
